import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FieldHint } from '@/components/shared/FieldHint';
import { SEARCH_CONFIG_FIELDS } from '@/lib/connectorFields';
import { useUpdateSearchConfig, useConnectorDbSchema, useSetEmbeddingProfile } from '@/hooks/useConnectors';
import type { VectorDBConnector, SearchConfig, DbSchemaTable, EmbeddingProvider } from '@/types/connector';

const POSTGRES_FAMILY = new Set(['pgvector', 'supabase', 'neon']);

const COLUMN_FIELDS = new Set(['vector_column', 'id_column', 'content_column']);

const EMBEDDING_PROVIDERS: { value: EmbeddingProvider; label: string }[] = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'openai_compatible', label: 'OpenAI-compatible (Ollama, TEI, vLLM)' },
  { value: 'cohere', label: 'Cohere' },
  { value: 'voyage', label: 'Voyage' },
];

/**
 * Item F: declare the embedding model a bring-your-own corpus was built
 * with, so server-side query embedding (`_embed_query`) matches the
 * corpus's vector space. Write-once: once `ingestion_config.embedding_provider`
 * is set, the section renders read-only.
 */
function EmbeddingProfileSection({ connector }: { connector: VectorDBConnector }) {
  const mutation = useSetEmbeddingProfile();
  const existingProvider = connector.ingestion_config?.embedding_provider;
  const [provider, setProvider] = useState<EmbeddingProvider>('openai');
  const [model, setModel] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (existingProvider) {
    const ic = connector.ingestion_config;
    return (
      <div className="space-y-2 rounded-md border p-3 bg-muted/30">
        <p className="text-sm font-medium">Embedding profile</p>
        <div className="text-xs text-muted-foreground space-y-0.5">
          <p>Provider: <span className="font-medium text-foreground">{existingProvider}</span></p>
          {ic?.embedding_model && (
            <p>Model: <span className="font-medium text-foreground">{ic.embedding_model}</span></p>
          )}
          {ic?.embedding_dimensions != null && (
            <p>Dimensions: <span className="font-medium text-foreground">{ic.embedding_dimensions}</span></p>
          )}
          {ic?.embedding_base_url && (
            <p>Base URL: <span className="font-medium text-foreground">{ic.embedding_base_url}</span></p>
          )}
        </div>
        <FieldHint>
          This is set once per connector and cannot be changed. Create a new connector to use a
          different embedding model.
        </FieldHint>
      </div>
    );
  }

  function handleSave() {
    setError(null);
    setSaved(false);
    if (provider === 'openai_compatible' && (!baseUrl.trim() || !model.trim())) {
      setError('openai_compatible requires both a base URL and a model.');
      return;
    }
    mutation.mutate(
      {
        id: connector.id,
        body: {
          provider,
          model: model.trim() || undefined,
          dimensions: dimensions.trim() ? parseInt(dimensions, 10) : undefined,
          base_url: baseUrl.trim() || undefined,
        },
      },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save embedding profile'),
      },
    );
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="space-y-1">
        <p className="text-sm font-medium">Embedding profile</p>
        <FieldHint>
          If you connected an existing corpus, declare the embedding model it was built with so
          text queries match your vectors. This is set once and cannot be changed afterward.
        </FieldHint>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Provider</label>
        <select
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={provider}
          onChange={(e) => setProvider(e.target.value as EmbeddingProvider)}
        >
          {EMBEDDING_PROVIDERS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">
          Model
          {provider === 'openai_compatible' && <span className="text-destructive ml-1">*</span>}
        </label>
        <Input
          placeholder="text-embedding-3-small"
          value={model}
          onChange={(e) => setModel(e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Dimensions (optional)</label>
        <Input
          type="number"
          placeholder="1536"
          value={dimensions}
          onChange={(e) => setDimensions(e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">
          Base URL
          {provider === 'openai_compatible' && <span className="text-destructive ml-1">*</span>}
        </label>
        <Input
          placeholder="https://your-host/v1"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </div>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 rounded p-2">{error}</p>
      )}
      {saved && (
        <p className="text-sm text-status-allow">Embedding profile saved.</p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleSave}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? 'Saving...' : 'Save embedding profile'}
      </Button>
    </div>
  );
}

interface SearchConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connector: VectorDBConnector;
}

export function SearchConfigDialog({ open, onOpenChange, connector }: SearchConfigDialogProps) {
  const fields = useMemo(() => SEARCH_CONFIG_FIELDS[connector.type] || [], [connector.type]);
  const mutation = useUpdateSearchConfig();
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const isPostgres = POSTGRES_FAMILY.has(connector.type);
  const isConnected = connector.status === 'connected';
  const schemaEnabled = open && isPostgres && isConnected;

  const { data: dbSchema, isLoading: schemaLoading } = useConnectorDbSchema(
    connector.id,
    schemaEnabled,
  );

  const selectedTable: DbSchemaTable | undefined = useMemo(() => {
    if (!dbSchema) return undefined;
    return dbSchema.tables.find((t) => t.name === values['table_name']);
  }, [dbSchema, values]);

  useEffect(() => {
    if (open) {
      const initial: Record<string, string> = {};
      for (const field of fields) {
        const existing = connector.search_config?.[field.name];
        if (existing != null) {
          initial[field.name] = Array.isArray(existing) ? existing.join(', ') : String(existing);
        } else {
          initial[field.name] = '';
        }
      }
      setValues(initial);
      setError(null);
    }
  }, [open, connector.id, connector.search_config, fields]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    for (const field of fields) {
      if (field.required && !values[field.name]?.trim()) {
        setError(`${field.label} is required`);
        return;
      }
    }

    const search_config: SearchConfig = {};
    for (const field of fields) {
      const val = values[field.name]?.trim();
      if (!val) continue;
      if (field.type === 'number') {
        search_config[field.name] = parseInt(val, 10);
      } else if (field.name === 'properties' || field.name === 'output_fields') {
        search_config[field.name] = val.split(',').map((s: string) => s.trim()).filter(Boolean);
      } else {
        search_config[field.name] = val;
      }
    }

    mutation.mutate(
      { id: connector.id, search_config },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save'),
      },
    );
  }

  function renderField(field: (typeof fields)[number]) {
    const value = values[field.name] || '';

    // Static select (e.g. distance metric)
    if (field.type === 'select' && field.options) {
      return (
        <select
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={value || field.placeholder}
          onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
        >
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    // Postgres-family: table_name → live dropdown from introspected schema
    if (isPostgres && field.name === 'table_name') {
      if (schemaLoading) {
        return (
          <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
            Loading tables…
          </div>
        );
      }
      if (dbSchema && dbSchema.tables.length > 0) {
        return (
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={value}
            onChange={(e) => {
              const tname = e.target.value;
              // Auto-suggest first vector column when table changes
              const table = dbSchema.tables.find((t) => t.name === tname);
              const firstVector = table?.columns.find((c) => c.type === 'vector');
              setValues((prev) => ({
                ...prev,
                table_name: tname,
                ...(firstVector && !prev['vector_column'] ? { vector_column: firstVector.name } : {}),
              }));
            }}
          >
            <option value="">— select a table —</option>
            {dbSchema.tables.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} ({t.columns.filter((c) => c.type === 'vector').length} vector col
                {t.columns.filter((c) => c.type === 'vector').length !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
        );
      }
      // Fallback to free text if schema unavailable
    }

    // Postgres-family: column fields → dropdown from selected table's columns
    if (isPostgres && COLUMN_FIELDS.has(field.name) && selectedTable) {
      const allCols = selectedTable.columns;
      const relevantCols =
        field.name === 'vector_column'
          ? allCols.filter((c) => c.type === 'vector')
          : allCols;

      if (relevantCols.length > 0) {
        return (
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={value}
            onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
          >
            {!field.required && <option value="">— none —</option>}
            {relevantCols.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}{c.type === 'vector' ? '' : ` (${c.type})`}
              </option>
            ))}
          </select>
        );
      }
    }

    return (
      <Input
        type={field.type === 'number' ? 'number' : 'text'}
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Search: {connector.name}</DialogTitle>
        </DialogHeader>
        {isPostgres && isConnected && !schemaLoading && dbSchema && (
          <p className="text-xs text-muted-foreground -mt-2">
            {dbSchema.tables.length > 0
              ? `${dbSchema.tables.length} vector-enabled table${dbSchema.tables.length !== 1 ? 's' : ''} found. Select from the dropdowns below.`
              : 'No vector-enabled tables found. Enter values manually or add a vector column to a table.'}
          </p>
        )}
        {isPostgres && !isConnected && (
          <p className="text-xs text-status-warning -mt-2">
            Run a connection test first to enable table and column auto-discovery.
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map((field) => (
            <div key={field.name} className="space-y-1">
              <label className="text-sm font-medium">
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </label>
              {renderField(field)}
              {field.hint && (
                <FieldHint tone={field.name === 'expected_dimension' ? 'warning' : 'muted'}>
                  {field.hint}
                </FieldHint>
              )}
            </div>
          ))}

          <EmbeddingProfileSection connector={connector} />

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded p-2">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
